var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isControlReadable, } from './Control';
import { convertVideoOutputs } from './DriverDetected';
import { assertIsQueryParam, isQueryParam, } from './EquipmentServiceDriver';
import { handleTransportError, } from './ParamBridge';
import { State } from './State';
import { KeyedValueBridge, ValueBridge } from './ValueBridge';
// This is like VideoInputBridge, but with collections. It is currently used for contentState and
// contentStop, but could be used to replace contentState with videoInputState one day.
//
// Note: this class is very similar to MediaBridge. They can probably be combined into one.
export class ContentStateBridge extends KeyedValueBridge {
    constructor(name, state, control, videoInputs, controlInfo, powerState, transport, valueCallback, framework, log) {
        super(name, state, control, () => this.keys, controlInfo, powerState, transport, valueCallback, log);
        this.videoInputs = videoInputs;
        this.framework = framework;
        this.keys = [];
        this.keys = this.computeKeys();
        // If the videoInputs state changes, then recompute our keys, delete any entries that were
        // removed, and resend any values for which the code <-> srcId mapping changed.
        if (isQueryParam(name) && this.videoInputs instanceof State) {
            this.videoInputsStateSubscription = this.videoInputs.subscribe((prev) => {
                if (this.framework.phase === 'controlling') {
                    const curr = this.videoInputs;
                    for (const key of this.keys) {
                        if (!(key in curr))
                            this.state.update(key, undefined);
                    }
                    this.keys = this.computeKeys();
                    for (const [srcId, videoInput] of Object.entries(curr.value)) {
                        const prevSrcId = this.findSrcId(videoInput.code, prev);
                        if (srcId !== prevSrcId) {
                            const code = videoInput.code;
                            const value = this.convertToParam(code, this.state.values[code]);
                            this.valueCallback(this.name, value);
                        }
                    }
                }
            });
        }
    }
    convertFromParam(value) {
        const videoInputs = this.videoInputs instanceof State ? this.videoInputs.value : this.videoInputs;
        for (const [srcId, videoInput] of Object.entries(videoInputs)) {
            const driverValue = value.state === null ? 'none' : value.state;
            if (srcId === value.id)
                return { key: videoInput.code, value: driverValue };
        }
        throw new Error(`${this.name}: no video input with srcId = ${value.id}`);
    }
    convertToParam(key, value) {
        const videoInputs = this.videoInputs instanceof State ? this.videoInputs.value : this.videoInputs;
        for (const [srcId, videoInput] of Object.entries(videoInputs)) {
            const state = value === 'none' ? null : value;
            if (videoInput.code === key)
                return { id: srcId, state };
        }
        throw new Error(`${this.name}: no video input with code = ${key}`);
    }
    computeKeys() {
        const videoInputs = this.videoInputs instanceof State ? this.videoInputs.value : this.videoInputs;
        return Object.keys(videoInputs)
            .sort()
            .map(key => videoInputs[key].code);
    }
    destroy() {
        if (this.videoInputs instanceof State && this.videoInputsStateSubscription !== undefined) {
            this.videoInputs.unsubscribe(this.videoInputsStateSubscription);
        }
        super.destroy();
    }
    findSrcId(code, videoInputs) {
        for (const [srcId, input] of Object.entries(videoInputs)) {
            if (input.code === code)
                return srcId;
        }
        return undefined;
    }
}
// EquipmentService sets the video input based on the srcId of the video input configuration. We
// translate between that srcId and the video input code stored for that srcId.
//
// For devices that provide a dynamic video configuration via the videoInputs control, we use the
// dynamic configuration.
//
// If the device's input code doesn't match anything in the device configuration, then we send
// null to EquipmentService. If EquipmentService tries to set a null or non-existent srcId, then
// set the device's input code to its current value.
//
// This bridge is also used for contentSource. I should probably combine them into one class. Or
// even better, one day just remove the contentSource and contentState controls and replace them
// with videoInput and a new videoInputState control. I don't think we need separate controls for
// them. We'd still need to keep contentStop though.
export class VideoInputBridge extends ValueBridge {
    constructor(name, state, control, videoInputs, controlInfo, powerState, transport, valueCallback, framework, log) {
        super(name, state, control, controlInfo, powerState, transport, valueCallback, log);
        this.videoInputs = videoInputs;
        this.framework = framework;
        // If the videoInputs state changes, then recompute and resend the videoInput value if
        // the code <-> srcId mapping changed.
        if (isControlReadable(this.control) && this.videoInputs instanceof State) {
            assertIsQueryParam(name);
            this.videoInputsStateSubscription = this.videoInputs.subscribe((prev) => {
                if (this.framework.phase === 'controlling') {
                    const newSrcId = this.convertToParam(this.state.value);
                    if (newSrcId !== this.srcId)
                        this.onQuery(name);
                }
            });
        }
    }
    convertFromParam(srcId) {
        if (srcId === null) {
            this.log.error(`${this.name}: srcId is null`);
            return this.state.value;
        }
        const videoInput = this.videoInputs instanceof State
            ? this.videoInputs.value[srcId]
            : this.videoInputs[srcId];
        if (!videoInput) {
            this.log.error(`${this.name}: no video input config for srcId: ${srcId}`);
            return this.state.value;
        }
        return videoInput.code;
    }
    convertToParam(code) {
        const videoInputs = this.videoInputs instanceof State ? this.videoInputs.value : this.videoInputs;
        for (const [srcId, videoInput] of Object.entries(videoInputs)) {
            if (videoInput.code === code) {
                this.srcId = srcId;
                return srcId;
            }
        }
        this.log.info(`${this.name}: no video input configured for code: ${code}`);
        this.srcId = undefined;
        return null;
    }
    destroy() {
        if (this.videoInputs instanceof State && this.videoInputsStateSubscription !== undefined) {
            this.videoInputs.unsubscribe(this.videoInputsStateSubscription);
        }
        super.destroy();
    }
}
// Note: this class is very similar to ContentStateBridge. They can probably be combined into one.
export class VideoInputKeyedBridge extends KeyedValueBridge {
    constructor(name, config, control, state, videoInputs, controlInfo, powerState, transport, valueCallback, framework, log, addValueToTag) {
        super(name, state, control, () => this.computeKeys(), controlInfo, powerState, transport, valueCallback, log, addValueToTag);
        this.config = config;
        this.videoInputs = videoInputs;
        this.framework = framework;
        this.keys = [];
        // If the videoInputs state changes, then recompute our keys, delete any entries that were
        // removed, and resend any values for which the code <-> srcId mapping changed.
        if (isQueryParam(name) && this.videoInputs instanceof State) {
            this.videoInputsStateSubscription = this.videoInputs.subscribe((prev) => {
                if (this.framework.phase === 'controlling') {
                    const curr = this.videoInputs;
                    for (const key of this.keys) {
                        if (!(key in curr))
                            this.state.update(key, undefined);
                    }
                    this.keys = this.computeKeys();
                    for (const [srcId, videoInput] of Object.entries(curr.value)) {
                        const prevSrcId = this.findSrcId(videoInput.code, prev);
                        if (srcId !== prevSrcId) {
                            const code = videoInput.code;
                            const value = this.convertToParam(code, this.state.values[code]);
                            this.valueCallback(this.name, value);
                        }
                    }
                }
            });
        }
    }
    convertFromParam(value) {
        const videoInputs = this.videoInputs instanceof State ? this.videoInputs.value : this.videoInputs;
        let key;
        for (const [videoInputId, videoInput] of Object.entries(videoInputs)) {
            if (videoInputId === value.videoInputId) {
                key = videoInput.code;
                break;
            }
        }
        if (key === undefined)
            throw new Error(`no videoInput for id "${value.videoInputId}"`);
        if (typeof value !== 'object') {
            throw new Error(`incompatible type for VideoInputKeyedBridge`);
        }
        const convertedValue = Object.assign({}, value);
        delete convertedValue.videoInputId;
        return { key, value: convertedValue };
    }
    convertToParam(key, value) {
        const videoInputs = this.videoInputs instanceof State ? this.videoInputs.value : this.videoInputs;
        if (typeof value !== 'boolean' && typeof value !== 'number' && typeof value !== 'string') {
            throw new Error(`incompatible type for VideoInputKeyedBridge`);
        }
        for (const [videoInputId, videoInput] of Object.entries(videoInputs)) {
            if (videoInput.code === key)
                return { videoInputId, value };
        }
        throw new Error(`${this.name}: no video input with code = ${key}`);
    }
    // This gets called in the base class getValues method, which is called to query the control's
    // initial values.
    computeKeys() {
        this.keys = (this.control.getKeys
            ? this.control.getKeys(this.config)
            : Object.values(this.videoInputs instanceof State ? this.videoInputs.value : this.videoInputs).map(videoInput => videoInput.code)).sort();
        return this.keys;
    }
    destroy() {
        if (this.videoInputs instanceof State && this.videoInputsStateSubscription !== undefined) {
            this.videoInputs.unsubscribe(this.videoInputsStateSubscription);
        }
        super.destroy();
    }
    findSrcId(code, videoInputs) {
        for (const [srcId, input] of Object.entries(videoInputs)) {
            if (input.code === code)
                return srcId;
        }
        return undefined;
    }
    getKeymap() {
        const videoInputs = this.videoInputs instanceof State ? this.videoInputs.value : this.videoInputs;
        return Object.fromEntries(Object.entries(videoInputs).map(e => [e[0], e[1].code]));
    }
}
export class VideoInputsBridge {
    constructor(control, state, transport, framework, log) {
        this.control = control;
        this.state = state;
        this.transport = transport;
        this.framework = framework;
        this.log = log;
        this.pollName = 'videoInputs';
        this.queryHandlers = {};
        this.setFilters = {};
        this.setHandlers = {};
        this.getTag = 'get.videoInputs';
        this.stateSubscription = this.state.subscribe((prev) => {
            if (this.framework.phase === 'controlling')
                this.framework.recomputeCaps();
        }, false);
    }
    destroy() {
        this.state.unsubscribe(this.stateSubscription);
    }
    getInitialValues() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.getValues(false);
        });
    }
    poll() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.transport.hasCommands(this.getTag))
                return;
            yield this.getValues(true);
        });
    }
    getValues(isPoll) {
        return __awaiter(this, void 0, void 0, function* () {
            const tags = isPoll ? [this.getTag, 'poll'] : [this.getTag];
            try {
                this.state.update(yield this.control.get(tags));
            }
            catch (error) {
                handleTransportError(this.log, error);
            }
        });
    }
}
// EquipmentService expects the videoMatrixRoutes to be a Record<string, string>, where the key is
// the video output source id and the value is the video input source id. We translate between that
// structure and a map of video output code to video input code.
export class VideoMatrixRoutesBridge {
    constructor(config, state, control, detectedState, controlInfo, powerState, transport, valueCallback, log) {
        this.config = config;
        this.state = state;
        this.control = control;
        this.detectedState = detectedState;
        this.controlInfo = controlInfo;
        this.powerState = powerState;
        this.transport = transport;
        this.valueCallback = valueCallback;
        this.log = log;
        this.name = 'videoMatrixRoute';
        this.pollName = 'videoMatrixRoutes';
        this.queryHandlers = {};
        this.setFilters = {};
        this.setHandlers = {};
        this.pollCycle = 0;
        this.stateSubscription = this.state.subscribe((key, prevValue) => {
            const newValues = Object.assign({}, this.state.values);
            newValues[key] = this.state.values[key];
            this.valueCallback(this.name, this.convertToParam(newValues));
        });
        this.queryHandlers[this.name] = this.onQuery.bind(this);
        this.setFilters[this.name] = this.setFilter.bind(this);
        this.setHandlers[this.name] = this.onSet.bind(this);
    }
    destroy() {
        this.state.unsubscribe(this.stateSubscription);
    }
    getInitialValues() {
        return __awaiter(this, void 0, void 0, function* () {
            this.pollCycle = 0;
            yield this.getValues(false);
        });
    }
    poll() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.getValues(true);
        });
    }
    // We prefer to always use undefined instead of null, but EquipmentService is legacy code and
    // expects null.
    getId(config, code) {
        for (const id of Object.keys(config)) {
            if (config[id].code === code)
                return id;
        }
        return null;
    }
    getInCode(inId) {
        var _a, _b, _c;
        const inCode = (_c = ((_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.videoInputs) !== null && _b !== void 0 ? _b : {})[inId]) === null || _c === void 0 ? void 0 : _c.code;
        if (inCode === undefined) {
            this.log.error(`${this.pollName}: no video input config for id: ${inId}`);
        }
        return inCode;
    }
    getOutCode(outId) {
        var _a, _b;
        const outCode = (_b = ((_a = this.getVideoOutputs()) !== null && _a !== void 0 ? _a : {})[outId]) === null || _b === void 0 ? void 0 : _b.code;
        if (outCode === undefined) {
            this.log.error(`${this.pollName}: no video output config for id: ${outId}`);
        }
        return outCode;
    }
    getValues(isPoll) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.controlInfo.canGet(this.powerState.value))
                return;
            const allOutIds = Object.keys((_a = this.getVideoOutputs()) !== null && _a !== void 0 ? _a : {});
            if (this.pollCycle >= allOutIds.length)
                this.pollCycle = 0;
            const outIds = isPoll ? [allOutIds[this.pollCycle++]] : allOutIds;
            for (const outId of outIds) {
                const outCode = this.getOutCode(outId);
                if (outCode === undefined)
                    continue;
                const getTag = `get.${this.name}.${outCode}`;
                const tags = isPoll ? [getTag, 'poll'] : [getTag];
                try {
                    this.state.update(outCode, yield this.control.get(outCode, tags));
                }
                catch (error) {
                    handleTransportError(this.log, error);
                }
            }
        });
    }
    getVideoOutputs() {
        var _a, _b;
        if ('videoOutputs' in this.detectedState.value) {
            return convertVideoOutputs((_a = this.detectedState.value.videoOutputs) !== null && _a !== void 0 ? _a : []);
        }
        return (_b = this.config) === null || _b === void 0 ? void 0 : _b.videoOutputs;
    }
    onQuery(param) {
        assertIsQueryParam(param);
        this.valueCallback(this.name, this.convertToParam(this.state.values));
    }
    onSet(param, setValue) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.controlInfo.canSet(this.powerState.value)) {
                this.valueCallback(this.name, this.convertToParam(Object.assign({}, this.state.values)));
                return;
            }
            const value = setValue;
            const outCode = this.getOutCode(value.videoOutputId);
            const inCode = this.getInCode(value.videoInputId);
            if (outCode === undefined)
                return;
            if (inCode === undefined)
                return;
            const setTag = `set.${this.name}.${outCode}`;
            this.transport.removeCommands(['poll', setTag]);
            try {
                const newValue = yield this.control.set(outCode, inCode, [setTag]);
                this.state.update(outCode, newValue, true);
            }
            catch (error) {
                handleTransportError(this.log, error);
            }
        });
    }
    convertToParam(routes) {
        var _a;
        const inputsConfig = (_a = this.config) === null || _a === void 0 ? void 0 : _a.videoInputs;
        const outputsConfig = this.getVideoOutputs();
        const convertedRoutes = {};
        if (!inputsConfig) {
            this.log.info(`${this.pollName}: no video inputs config`);
            return convertedRoutes;
        }
        if (!outputsConfig) {
            this.log.error(`${this.pollName}: no video outputs config`);
            return convertedRoutes;
        }
        for (const outCode of Object.keys(routes)) {
            const outId = this.getId(outputsConfig, outCode);
            if (outId === null) {
                this.log.warning(`${this.pollName}: unmatched video output code: ${outCode}`);
                continue;
            }
            const inCode = routes[outCode];
            const inId = this.getId(inputsConfig, inCode);
            if (inId === null) {
                this.log.warning(`${this.pollName}: unmatched video input code: ${inCode}`);
            }
            convertedRoutes[outId] = inId;
        }
        return convertedRoutes;
    }
    setFilter(param, value, compParam, compValue) {
        if (param !== compParam)
            return true;
        const outputId = value.videoOutputId;
        const compOutputId = compValue.videoOutputId;
        return outputId !== compOutputId;
    }
}
